import type { ContentNode, DocumentState } from '../document-types';
import type { NodeHandler, RenderContext } from './types';

/**
 * Cooperating handlers that render a ProseMirror table as a GitHub-Flavored
 * Markdown table, so the model reads tabular data back as a table instead of a
 * run-on of cell text (the previous fallback just recursed children).
 *
 * The renderer hands each handler the markdown of its children already
 * concatenated into a single string, which has lost the cell/row boundaries. The
 * cell and row handlers re-introduce those boundaries with control-character
 * sentinels; the table handler splits on them to rebuild the grid. The sentinels
 * never reach the output — the table handler consumes them, and a cell strips any
 * that happen to appear in real content.
 */
const CELL_SEP = '\u0000';
const ROW_SEP = '\u0001';

/** Collapse a cell's rendered children into a single GFM-safe line. */
function toCellText(childContent: string): string {
  return (
    childContent
      // The row/cell sentinels are U+0000/U+0001 control characters, which do not
      // occur in real document prose. Strip any that somehow appear so they can
      // never be mistaken for a boundary. The tradeoff — these two control chars
      // are dropped from cell text — is acceptable: they are not meaningful
      // content and are invalid in most text contexts to begin with.
      .replace(/[\u0000\u0001]/g, '')
      .replace(/\r?\n+/g, ' ') // GFM cells are single-line
      // A literal '|' ends the cell, so every pipe is escaped — including inside
      // code spans. GFM resolves table-cell backslash escapes *before* inline
      // parsing, so `a\|b` becomes `a|b` and a code span `` `a|b` `` round-trips
      // intact; leaving a pipe unescaped (even in code) would split the row into
      // the wrong number of columns.
      .replace(/\|/g, '\\|')
      .replace(/ {2,}/g, ' ')
      .trim()
  );
}

export class TableCellHandler implements NodeHandler {
  readonly type = 'tableCell';

  render(
    _node: ContentNode | DocumentState,
    _parent: ContentNode | DocumentState | null,
    childContent: string,
    _ctx: RenderContext
  ): string {
    return toCellText(childContent) + CELL_SEP;
  }
}

export class TableHeaderHandler implements NodeHandler {
  readonly type = 'tableHeader';

  render(
    _node: ContentNode | DocumentState,
    _parent: ContentNode | DocumentState | null,
    childContent: string,
    _ctx: RenderContext
  ): string {
    return toCellText(childContent) + CELL_SEP;
  }
}

export class TableRowHandler implements NodeHandler {
  readonly type = 'tableRow';

  render(
    _node: ContentNode | DocumentState,
    _parent: ContentNode | DocumentState | null,
    childContent: string,
    _ctx: RenderContext
  ): string {
    return childContent + ROW_SEP;
  }
}

export class TableHandler implements NodeHandler {
  readonly type = 'table';

  render(
    _node: ContentNode | DocumentState,
    _parent: ContentNode | DocumentState | null,
    childContent: string,
    _ctx: RenderContext
  ): string {
    const grid = childContent
      .split(ROW_SEP)
      .filter((chunk) => chunk.length > 0)
      .map((chunk) => {
        const cells = chunk.split(CELL_SEP);
        // Every cell terminates with CELL_SEP, so the split leaves a trailing ''.
        if (cells.length > 0 && cells[cells.length - 1] === '') {
          cells.pop();
        }
        return cells;
      })
      .filter((row) => row.length > 0);

    if (grid.length === 0) {
      return '';
    }

    const cols = Math.max(...grid.map((row) => row.length));
    const line = (row: string[]): string =>
      '| ' +
      Array.from({ length: cols }, (_unused, i) => row[i] ?? '').join(' | ') +
      ' |';
    const separator =
      '| ' + Array.from({ length: cols }, () => '---').join(' | ') + ' |';

    // GFM has no header-less table, so the first row is always the header.
    const [headerRow = [], ...bodyRows] = grid;
    // Trailing '\n\n' matches the paragraph/heading handlers' block convention,
    // so a table is separated from the next block by a blank line. The renderer
    // trims trailing newlines on the final block.
    return (
      [line(headerRow), separator, ...bodyRows.map(line)].join('\n') + '\n\n'
    );
  }
}
